import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import Lenis from "lenis";
import Marquee from "react-fast-marquee";
import axios from "axios";
import { toast } from "sonner";
import {
  Moon,
  Heart,
  ShieldCheck,
  Globe,
  MessageCircle,
  Baby,
  MapPin,
  ArrowUpRight,
  Sparkles,
  Star,
  Quote,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { translations, WHATSAPP_NUMBER, WHATSAPP_DISPLAY } from "@/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IMG = {
  hero:
    "https://customer-assets-0z36b82j.emergentagent.net/job_sleep-care-portugal/artifacts/u5ppke0x_Denise%20-%20Hero.jpeg",
  aboutSpa:
    "https://customer-assets-0z36b82j.emergentagent.net/job_sleep-care-portugal/artifacts/pvo9pdy5_Denise%20-%20About.jpg",
  services:
    "https://images.unsplash.com/photo-1780329930345-19a7f25e5fc6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxtYXRlcm5hbCUyMGNhcmUlMjBiYWJ5fGVufDB8fHx8MTc4NjY0NDI1MHww&ixlib=rb-4.1.0&q=85",
  lisbon:
    "https://images.unsplash.com/photo-1509870449717-5609536a5393?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHw0fHxsaXNib24lMjBhcmNoaXRlY3R1cmUlMjBwYXN0ZWx8ZW58MHx8fHwxNzg2NjQ0MjUwfDA&ixlib=rb-4.1.0&q=85",
};

const SERVICE_ICONS = [Moon, Heart, ShieldCheck, Globe];

/* ---------- primitives ---------- */

const Reveal = ({ children, delay = 0, y = 28, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const MaskLine = ({ children, delay = 0 }) => (
  <span className="mask-reveal block">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Pill = ({ children, className = "", ...props }) => (
  <button
    className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition-[background-color,color,transform,box-shadow] duration-300 active:scale-95 ${className}`}
    {...props}
  >
    {children}
  </button>
);

/* ---------- header ---------- */

const Header = ({ lang, setLang, t, onWhatsapp }) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "about", label: t.nav.about },
    { id: "services", label: t.nav.services },
    { id: "testimonials", label: t.nav.testimonials },
    { id: "experience", label: t.nav.experience },
    { id: "contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled
          ? "bg-[#FAF7F2]/80 backdrop-blur-xl border-b border-[#E6DFD8]/60 shadow-[0_8px_30px_rgb(58,53,50,0.04)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1300px] px-6 lg:px-10 h-[76px] flex items-center justify-between">
        <a href="#top" data-testid="logo" className="flex flex-col leading-none">
          <span className="font-serif text-[22px] tracking-tight text-[#3A3532]">
            Denise Maria
          </span>
          <span className="font-script text-[16px] text-[#C17F74] -mt-1">
            cuidadora materno infantil
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={`nav-${l.id}`}
              className="text-sm text-[#756B66] hover:text-[#3A3532] transition-colors duration-300 relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#C17F74] transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1 text-sm rounded-full bg-[#F4E8E4]/70 px-1 py-1">
            {["pt", "en"].map((code) => (
              <button
                key={code}
                data-testid={`lang-toggle-${code}`}
                onClick={() => setLang(code)}
                className={`rounded-full px-3 py-1 uppercase tracking-wide transition-colors duration-300 ${
                  lang === code
                    ? "bg-white text-[#3A3532] shadow-sm font-semibold"
                    : "text-[#756B66] hover:text-[#3A3532]"
                }`}
              >
                {code}
              </button>
            ))}
          </div>
          <Pill
            data-testid="header-whatsapp"
            onClick={onWhatsapp}
            className="hidden sm:inline-flex bg-[#C17F74] text-white hover:bg-[#A9685E] shadow-[0_8px_24px_rgb(193,127,116,0.35)]"
          >
            <MessageCircle size={16} />
            {t.nav.cta}
          </Pill>
        </div>
      </div>
    </header>
  );
};

/* ---------- hero ---------- */

const Hero = ({ t, onWhatsapp, scrollTo }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "18%"]);
  const badgeY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "-40%"]);

  return (
    <section id="top" ref={ref} className="relative pt-[120px] lg:pt-[150px] pb-16 lg:pb-24 overflow-hidden">
      {/* soft blobs */}
      <div className="pointer-events-none absolute -top-24 -left-24 h-[380px] w-[380px] rounded-full bg-[#E8C5BE]/40 blur-[90px]" />
      <div className="pointer-events-none absolute top-40 right-0 h-[300px] w-[300px] rounded-full bg-[#F4E8E4] blur-[80px]" />

      <div className="relative mx-auto max-w-[1300px] px-6 lg:px-10 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#E6DFD8] bg-white/70 backdrop-blur px-4 py-2 text-xs tracking-wide text-[#756B66] mb-7"
          >
            <Sparkles size={13} className="text-[#C17F74]" />
            {t.hero.eyebrow}
          </motion.div>

          <h1 className="font-serif font-medium tracking-tight text-[#3A3532] text-5xl sm:text-6xl lg:text-[5.2rem] leading-[0.98]">
            <MaskLine delay={0.15}>{t.hero.line1}</MaskLine>
            <MaskLine delay={0.28}>{t.hero.line2}</MaskLine>
            <MaskLine delay={0.41}>
              <span className="italic text-[#C17F74]">{t.hero.line3}</span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-7 max-w-md text-[#756B66] text-base leading-relaxed"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Pill
              data-testid="hero-book"
              onClick={() => scrollTo("contact")}
              className="bg-[#C17F74] text-white hover:bg-[#A9685E] shadow-[0_10px_30px_rgb(193,127,116,0.35)] px-7 py-3.5"
            >
              {t.hero.ctaPrimary}
              <ArrowUpRight size={16} />
            </Pill>
            <Pill
              data-testid="hero-about"
              onClick={() => scrollTo("about")}
              className="border border-[#C17F74] text-[#C17F74] hover:bg-[#C17F74] hover:text-white px-7 py-3.5"
            >
              {t.hero.ctaSecondary}
            </Pill>
          </motion.div>
        </div>

        {/* image */}
        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-[2.2rem] overflow-hidden shadow-[0_30px_80px_rgb(58,53,50,0.12)] aspect-[4/5]"
          >
            <motion.img
              src={IMG.hero}
              alt="Denise Maria, cuidadora materno infantil e ama noturna em Lisboa"
              style={{ y: imgY }}
              className="h-[118%] w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#3A3532]/10 to-transparent" />
          </motion.div>

          <motion.div
            style={{ y: badgeY }}
            className="absolute -left-4 sm:-left-8 top-8 rounded-full bg-[#FAF7F2]/90 backdrop-blur-md border border-[#E6DFD8] px-5 py-3 shadow-[0_8px_30px_rgb(58,53,50,0.08)]"
          >
            <span className="font-serif text-2xl text-[#C17F74]">40+</span>
            <span className="block text-[11px] text-[#756B66] leading-tight max-w-[110px]">
              {t.hero.badge1}
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="absolute -right-3 sm:-right-6 bottom-8 flex items-center gap-3 rounded-2xl bg-white border border-[#E6DFD8] px-4 py-3 shadow-[0_8px_30px_rgb(58,53,50,0.08)] max-w-[200px]"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F4E8E4] text-[#C17F74]">
              <Heart size={16} />
            </span>
            <span className="text-[11px] text-[#756B66] leading-tight">
              {t.hero.badge2}
            </span>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ---------- marquee ---------- */

const Ribbon = ({ t }) => (
  <div className="border-y border-[#E6DFD8] bg-[#F4E8E4] py-5">
    <Marquee speed={40} gradient={false} className="marquee-track">
      {[0, 1].map((i) => (
        <span
          key={i}
          className="font-serif italic text-2xl md:text-3xl text-[#C17F74] mx-6"
        >
          {t.marquee}
        </span>
      ))}
    </Marquee>
  </div>
);

/* ---------- stats ---------- */

const Stats = ({ t }) => (
  <section id="experience" className="mx-auto max-w-[1300px] px-6 lg:px-10 py-24 lg:py-32">
    <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-12 lg:gap-20 items-end">
      <Reveal>
        <h2 className="font-serif text-4xl lg:text-5xl leading-tight tracking-tight text-[#3A3532]">
          {t.stats.title}
        </h2>
        <p className="mt-5 text-[#756B66] max-w-sm">{t.stats.sub}</p>
      </Reveal>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10">
        {t.stats.items.map((s, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className="border-t border-dashed border-[#E8C5BE] pt-5">
              <div className="font-serif text-5xl lg:text-6xl text-[#C17F74] leading-none">
                {s.value}
              </div>
              <div className="mt-3 text-sm text-[#756B66] leading-snug">
                {s.label}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- services ---------- */

const Services = ({ t }) => (
  <section id="services" className="bg-[#F4E8E4]/50 border-y border-[#E6DFD8]">
    <div className="mx-auto max-w-[1300px] px-6 lg:px-10 py-24 lg:py-32">
      <div className="grid lg:grid-cols-[1fr_0.9fr] gap-14 items-start">
        <div>
          <Reveal>
            <span className="font-script text-2xl text-[#C17F74]">
              {t.services.eyebrow}
            </span>
            <h2 className="mt-2 font-serif text-4xl lg:text-5xl tracking-tight text-[#3A3532] leading-[1.05]">
              {t.services.title}
            </h2>
          </Reveal>

          <div className="mt-12 space-y-2">
            {t.services.list.map((s, i) => {
              const Icon = SERVICE_ICONS[i];
              return (
                <Reveal key={i} delay={i * 0.06}>
                  <div
                    data-testid={`service-${i}`}
                    className="group grid grid-cols-[auto_1fr] gap-6 py-7 border-t border-dashed border-[#E8C5BE] transition-colors"
                  >
                    <div className="flex flex-col items-center gap-3">
                      <span className="font-serif text-2xl text-[#C17F74]/60">
                        {s.n}
                      </span>
                      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-[#C17F74] shadow-[0_6px_20px_rgb(58,53,50,0.06)] transition-transform duration-300 group-hover:scale-110 group-hover:bg-[#C17F74] group-hover:text-white">
                        <Icon size={20} />
                      </span>
                    </div>
                    <div>
                      <h3 className="font-serif text-2xl text-[#3A3532] leading-tight">
                        {s.title}
                      </h3>
                      <p className="mt-2 text-[#756B66] leading-relaxed max-w-lg">
                        {s.desc}
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        <Reveal delay={0.1} className="lg:sticky lg:top-28">
          <div className="relative rounded-[2.2rem] overflow-hidden aspect-[3/4] shadow-[0_30px_80px_rgb(58,53,50,0.12)]">
            <img
              src={IMG.services}
              alt="Cuidado materno"
              className="h-full w-full object-cover"
            />
            <div className="absolute bottom-5 left-5 right-5 rounded-2xl bg-[#FAF7F2]/85 backdrop-blur-md border border-white/50 px-5 py-4">
              <div className="flex items-center gap-3">
                <Baby size={20} className="text-[#C17F74]" />
                <p className="font-script text-2xl text-[#3A3532]">
                  {t.about.quote}
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------- about ---------- */

const About = ({ t }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const lisbonY = useTransform(scrollYProgress, [0, 1], ["-8%", reduce ? "-8%" : "10%"]);

  return (
    <section id="about" ref={ref} className="mx-auto max-w-[1300px] px-6 lg:px-10 py-24 lg:py-32">
      <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        {/* image cluster */}
        <div className="relative">
          <div className="relative rounded-[2.2rem] overflow-hidden aspect-[4/5] shadow-[0_30px_80px_rgb(58,53,50,0.14)]">
            <img
              src={IMG.aboutSpa}
              alt="Denise Maria a dar banho a um recém-nascido — cuidados materno infantis em Lisboa"
              className="h-full w-full object-cover"
            />
          </div>

          <motion.div
            style={{ y: lisbonY }}
            className="absolute -bottom-10 -right-4 sm:-right-8 w-40 sm:w-52 rounded-[1.6rem] overflow-hidden border-4 border-[#FAF7F2] shadow-[0_20px_50px_rgb(58,53,50,0.18)] aspect-[3/4]"
          >
            <img src={IMG.lisbon} alt="Lisboa" className="h-full w-full object-cover" />
          </motion.div>

          <div className="absolute -top-5 -left-3 rounded-full bg-white border border-[#E6DFD8] px-4 py-2 flex items-center gap-2 shadow-[0_8px_24px_rgb(58,53,50,0.08)]">
            <MapPin size={14} className="text-[#C17F74]" />
            <span className="text-xs text-[#756B66]">Lisboa, Portugal</span>
          </div>
        </div>

        {/* text */}
        <div>
          <Reveal>
            <span className="font-script text-2xl text-[#C17F74]">{t.about.eyebrow}</span>
            <h2 className="mt-1 font-serif text-5xl lg:text-6xl tracking-tight text-[#3A3532] leading-none">
              {t.about.name}
            </h2>
            <p className="mt-3 text-sm uppercase tracking-[0.2em] text-[#756B66]">
              {t.about.role}
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <p className="mt-8 text-lg text-[#756B66] leading-relaxed">{t.about.p1}</p>
            <p className="mt-5 text-lg text-[#756B66] leading-relaxed">{t.about.p2}</p>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-10 flex items-center justify-between border-t border-dashed border-[#E8C5BE] pt-6">
              <span className="font-script text-4xl text-[#3A3532]">{t.about.signature}</span>
              <Heart size={22} className="text-[#C17F74]" fill="#C17F74" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ---------- testimonials ---------- */

const Stars = ({ n = 5 }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: n }).map((_, i) => (
      <Star key={i} size={15} className="text-[#C17F74]" fill="#C17F74" />
    ))}
  </div>
);

const Testimonials = ({ t, lang }) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios
      .get(`${API}/testimonials`)
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, []);

  if (!items.length) return null;

  return (
    <section id="testimonials" className="mx-auto max-w-[1300px] px-6 lg:px-10 py-24 lg:py-32">
      <Reveal className="max-w-2xl">
        <span className="font-script text-2xl text-[#C17F74]">{t.testimonials.eyebrow}</span>
        <h2 className="mt-1 font-serif text-4xl lg:text-5xl tracking-tight text-[#3A3532] leading-[1.05]">
          {t.testimonials.title}
        </h2>
        <p className="mt-5 text-[#756B66]">{t.testimonials.sub}</p>
      </Reveal>

      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((it, i) => (
          <Reveal key={it.id} delay={(i % 3) * 0.08}>
            <figure
              data-testid={`testimonial-${i}`}
              className="h-full rounded-[2rem] bg-white border border-[#E6DFD8] p-8 shadow-[0_8px_30px_rgb(58,53,50,0.05)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgb(58,53,50,0.1)] flex flex-col"
            >
              <Quote size={30} className="text-[#E8C5BE]" />
              <blockquote className="mt-4 flex-1 text-[#3A3532] leading-relaxed font-serif text-xl">
                “{lang === "pt" ? it.quote_pt : it.quote_en}”
              </blockquote>
              <figcaption className="mt-6 pt-5 border-t border-dashed border-[#E8C5BE] flex items-center gap-3">
                {it.photo ? (
                  <img
                    src={it.photo}
                    alt={it.author}
                    className="h-12 w-12 rounded-full object-cover border-2 border-[#F4E8E4]"
                  />
                ) : (
                  <span className="h-12 w-12 rounded-full bg-[#F4E8E4] flex items-center justify-center text-[#C17F74]">
                    <Heart size={18} fill="#C17F74" />
                  </span>
                )}
                <div>
                  <div className="text-sm font-semibold text-[#3A3532]">{it.author}</div>
                  <div className="text-xs text-[#756B66] flex items-center gap-1">
                    <MapPin size={11} className="text-[#C17F74]" />
                    {it.location}
                  </div>
                </div>
                <div className="ml-auto">
                  <Stars n={it.rating || 5} />
                </div>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

/* ---------- contact ---------- */

const Contact = ({ t, onWhatsapp }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const lang = t.footer.tagline === translations.pt.footer.tagline ? "pt" : "en";

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, lang });
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "bg-white/80 border-[#E6DFD8] rounded-2xl h-12 px-4 text-[#3A3532] placeholder:text-[#B5ABA4] focus-visible:ring-[#C17F74]/40 focus-visible:border-[#C17F74]";

  return (
    <section id="contact" className="bg-[#C17F74] text-white">
      <div className="mx-auto max-w-[1300px] px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20">
          <div>
            <Reveal>
              <span className="font-script text-2xl text-white/85">{t.contact.eyebrow}</span>
              <h2 className="mt-1 font-serif text-4xl lg:text-6xl tracking-tight leading-[1.02]">
                {t.contact.title}
              </h2>
              <p className="mt-6 text-white/85 max-w-md leading-relaxed">{t.contact.sub}</p>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-10 flex flex-col gap-4">
                <button
                  data-testid="contact-whatsapp"
                  onClick={onWhatsapp}
                  className="group flex items-center justify-between gap-4 rounded-[1.6rem] bg-white text-[#3A3532] px-6 py-5 transition-transform duration-300 hover:scale-[1.02] active:scale-95 shadow-[0_16px_40px_rgb(0,0,0,0.12)]"
                >
                  <span className="flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#25D366] text-white">
                      <MessageCircle size={20} />
                    </span>
                    <span className="text-left">
                      <span className="block text-xs text-[#756B66]">{t.contact.whatsapp}</span>
                      <span className="block font-serif text-xl">{WHATSAPP_DISPLAY}</span>
                    </span>
                  </span>
                  <ArrowUpRight size={22} className="text-[#C17F74] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </button>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="rounded-[2rem] bg-[#FAF7F2] p-7 sm:p-9 shadow-[0_30px_80px_rgb(0,0,0,0.15)]"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#756B66] mb-1.5">{t.contact.name}</label>
                  <Input data-testid="input-name" required value={form.name} onChange={update("name")} className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm text-[#756B66] mb-1.5">{t.contact.phone}</label>
                  <Input data-testid="input-phone" required value={form.phone} onChange={update("phone")} className={inputCls} />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm text-[#756B66] mb-1.5">{t.contact.email}</label>
                <Input data-testid="input-email" type="email" required value={form.email} onChange={update("email")} className={inputCls} />
              </div>
              <div className="mt-4">
                <label className="block text-sm text-[#756B66] mb-1.5">{t.contact.message}</label>
                <Textarea
                  data-testid="input-message"
                  required
                  rows={4}
                  value={form.message}
                  onChange={update("message")}
                  placeholder={t.contact.messagePlaceholder}
                  className="bg-white/80 border-[#E6DFD8] rounded-2xl px-4 py-3 text-[#3A3532] placeholder:text-[#B5ABA4] focus-visible:ring-[#C17F74]/40 focus-visible:border-[#C17F74] resize-none"
                />
              </div>
              <button
                data-testid="submit-contact"
                type="submit"
                disabled={loading}
                className="mt-6 w-full rounded-full bg-[#C17F74] text-white py-4 font-medium transition-[background-color,transform] duration-300 hover:bg-[#A9685E] active:scale-[0.98] disabled:opacity-60"
              >
                {loading ? t.contact.sending : t.contact.send}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ---------- footer ---------- */

const Footer = ({ t }) => (
  <footer className="bg-[#FAF7F2] border-t border-[#E6DFD8]">
    <div className="mx-auto max-w-[1300px] px-6 lg:px-10 py-14">
      <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
        <div>
          <div className="font-serif text-3xl tracking-tight text-[#3A3532]">Denise Maria</div>
          <div className="font-script text-xl text-[#C17F74] -mt-1">{t.footer.tagline}</div>
          <div className="mt-4 flex items-center gap-2 text-sm text-[#756B66]">
            <MapPin size={14} className="text-[#C17F74]" />
            {t.footer.location}
          </div>
        </div>
        <div className="text-sm text-[#756B66] md:text-right">
          <a href={`https://wa.me/${WHATSAPP_NUMBER}`} className="block hover:text-[#C17F74] transition-colors">
            {WHATSAPP_DISPLAY}
          </a>
          <a href="https://cuidadorainfantil.com" className="block hover:text-[#C17F74] transition-colors">
            cuidadorainfantil.com
          </a>
        </div>
      </div>
      <div className="mt-10 pt-6 border-t border-dashed border-[#E8C5BE] text-xs text-[#B5ABA4]">
        © {new Date().getFullYear()} Denise Maria · {t.footer.rights}
      </div>
    </div>
  </footer>
);

/* ---------- root ---------- */

export default function Landing() {
  const [lang, setLang] = useState("pt");

  useEffect(() => {
    const saved = localStorage.getItem("dm_lang");
    if (saved === "pt" || saved === "en") {
      setLang(saved);
      return;
    }
    let cancelled = false;
    const fallback = () => {
      const nav = (navigator.language || "pt").toLowerCase();
      return nav.startsWith("pt") ? "pt" : "en";
    };
    axios
      .get(`${API}/geo`, { timeout: 4000 })
      .then((r) => {
        if (cancelled) return;
        setLang(r.data && r.data.lang ? r.data.lang : fallback());
      })
      .catch(() => {
        if (!cancelled) setLang(fallback());
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const changeLang = (code) => {
    setLang(code);
    localStorage.setItem("dm_lang", code);
  };

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  const t = translations[lang];

  useEffect(() => {
    document.documentElement.lang = lang;
    const meta = {
      pt: {
        title: "Denise Maria | Ama Noturna & Cuidadora Materno Infantil em Lisboa",
        desc: "Ama noturna e cuidadora materno infantil em Lisboa com mais de 40 anos de experiência. Especialista no sono do recém-nascido, apoio ao aleitamento e cuidados pós-parto. Agende uma visita.",
      },
      en: {
        title: "Denise Maria | Night Nanny & Newborn Care Specialist in Lisbon",
        desc: "Night nanny and maternal-infant caregiver in Lisbon with 40+ years of experience. Newborn sleep specialist, breastfeeding support and postpartum care. Book a visit.",
      },
    };
    document.title = meta[lang].title;
    const d = document.querySelector('meta[name="description"]');
    if (d) d.setAttribute("content", meta[lang].desc);
  }, [lang]);

  const openWhatsapp = () => {
    const msg = encodeURIComponent(t.contact.wpMessage);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`, "_blank");
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="relative">
      <Header lang={lang} setLang={changeLang} t={t} onWhatsapp={openWhatsapp} />
      <main>
        <Hero t={t} onWhatsapp={openWhatsapp} scrollTo={scrollTo} />
        <Ribbon t={t} />
        <Stats t={t} />
        <Services t={t} />
        <Testimonials t={t} lang={lang} />
        <About t={t} />
        <Contact t={t} onWhatsapp={openWhatsapp} />
      </main>
      <Footer t={t} />
    </div>
  );
}
